import type { PropertyType, ServiceFrequency } from '../enums';
import { roundCents, type Cents } from './money';

/**
 * Configurable pricing engine. It produces an INTERNAL estimate; the official
 * price of a quote is always set (and can be changed) by staff.
 */
export interface PricingExtra {
  code: string;
  labelFr: string;
  labelEn: string;
  priceCents: Cents;
  minutes: number;
}

export interface PricingConfig {
  basePriceByPropertyType: Record<PropertyType, Cents>;
  perBedroomCents: Cents;
  perBathroomCents: Cents;
  perKitchenCents: Cents;
  perFamilyRoomCents: Cents;
  perExtraFloorCents: Cents;
  /** Surcharge per square foot above the threshold. */
  sqftThreshold: number;
  perSqftAboveCents: number;
  petSurchargeCents: Cents;
  /** Discount ratio (0.1 = 10%) by service frequency. */
  frequencyDiscounts: Record<ServiceFrequency, number>;
  extras: PricingExtra[];
  minutes: {
    base: number;
    perBedroom: number;
    perBathroom: number;
    perKitchen: number;
    perFamilyRoom: number;
    perExtraFloor: number;
    pets: number;
  };
  minimumPriceCents: Cents;
  roundToCents: Cents;
  /** Above this duration a second worker is recommended. */
  secondWorkerAboveMinutes: number;
}

export const DEFAULT_PRICING_CONFIG: PricingConfig = {
  basePriceByPropertyType: { HOUSE: 9000, CONDO: 7500, APARTMENT: 7000, COMMERCIAL: 12000, OTHER: 8000 },
  perBedroomCents: 1500,
  perBathroomCents: 2000,
  perKitchenCents: 1500,
  perFamilyRoomCents: 1000,
  perExtraFloorCents: 1000,
  sqftThreshold: 2000,
  perSqftAboveCents: 3,
  petSurchargeCents: 1000,
  frequencyDiscounts: { ONE_TIME: 0, WEEKLY: 0.15, BIWEEKLY: 0.1, MONTHLY: 0.05, CUSTOM: 0.05 },
  extras: [
    { code: 'OVEN', labelFr: 'Intérieur du four', labelEn: 'Inside oven', priceCents: 3000, minutes: 30 },
    { code: 'FRIDGE', labelFr: 'Intérieur du réfrigérateur', labelEn: 'Inside fridge', priceCents: 3000, minutes: 30 },
    { code: 'WINDOWS', labelFr: 'Fenêtres intérieures', labelEn: 'Interior windows', priceCents: 4000, minutes: 45 },
    { code: 'BEDDING', labelFr: 'Changement de literie', labelEn: 'Bed linen change', priceCents: 1500, minutes: 15 },
    { code: 'BASEMENT', labelFr: 'Sous-sol', labelEn: 'Basement', priceCents: 3500, minutes: 40 },
    { code: 'CABINETS', labelFr: 'Intérieur des armoires', labelEn: 'Inside cabinets', priceCents: 3500, minutes: 40 },
  ],
  minutes: { base: 45, perBedroom: 15, perBathroom: 20, perKitchen: 20, perFamilyRoom: 10, perExtraFloor: 10, pets: 15 },
  minimumPriceCents: 8000,
  roundToCents: 500,
  secondWorkerAboveMinutes: 210,
};

export interface PricingInput {
  propertyType: PropertyType;
  bedrooms: number;
  bathrooms: number;
  kitchens: number;
  familyRooms: number;
  floors: number;
  squareFeet?: number | null;
  hasPets: boolean;
  frequency: ServiceFrequency;
  /** Service type multipliers (e.g. deep cleaning 1.5x). */
  servicePriceMultiplier: number;
  serviceDurationMultiplier: number;
  extras: string[];
}

export interface PricingLine {
  code: string;
  label: string;
  quantity: number;
  unitCents: Cents;
  totalCents: Cents;
}

export interface PricingEstimate {
  lines: PricingLine[];
  subtotalCents: Cents;
  discountCents: Cents;
  totalCents: Cents;
  estimatedMinutes: number;
  recommendedWorkers: number;
}

export function estimatePrice(input: PricingInput, config: PricingConfig = DEFAULT_PRICING_CONFIG, lang: 'fr' | 'en' = 'fr'): PricingEstimate {
  const fr = lang === 'fr';
  const lines: PricingLine[] = [];
  const push = (code: string, label: string, quantity: number, unitCents: Cents) => {
    if (quantity <= 0 || unitCents === 0) return;
    lines.push({ code, label, quantity, unitCents, totalCents: Math.round(quantity * unitCents) });
  };

  push('BASE', fr ? 'Prix de base' : 'Base price', 1, config.basePriceByPropertyType[input.propertyType]);
  push('BEDROOMS', fr ? 'Chambres' : 'Bedrooms', input.bedrooms, config.perBedroomCents);
  push('BATHROOMS', fr ? 'Salles de bain' : 'Bathrooms', input.bathrooms, config.perBathroomCents);
  push('KITCHENS', fr ? 'Cuisines' : 'Kitchens', input.kitchens, config.perKitchenCents);
  push('FAMILY_ROOMS', fr ? 'Espaces familiaux' : 'Family rooms', input.familyRooms, config.perFamilyRoomCents);
  push('FLOORS', fr ? 'Étages supplémentaires' : 'Extra floors', Math.max(0, input.floors - 1), config.perExtraFloorCents);
  if (input.squareFeet && input.squareFeet > config.sqftThreshold) {
    push('SQFT', fr ? 'Superficie additionnelle (pi²)' : 'Additional area (sq ft)', input.squareFeet - config.sqftThreshold, config.perSqftAboveCents);
  }
  if (input.hasPets) push('PETS', fr ? 'Animaux' : 'Pets', 1, config.petSurchargeCents);

  // The service multiplier applies to the property-based part, not to extras.
  const propertySubtotal = lines.reduce((s, l) => s + l.totalCents, 0);
  const serviceAdjustment = Math.round(propertySubtotal * (input.servicePriceMultiplier - 1));
  if (serviceAdjustment !== 0) {
    lines.push({
      code: 'SERVICE',
      label: fr ? 'Ajustement type de service' : 'Service type adjustment',
      quantity: 1,
      unitCents: serviceAdjustment,
      totalCents: serviceAdjustment,
    });
  }

  let extraMinutes = 0;
  for (const code of input.extras) {
    const extra = config.extras.find((e) => e.code === code);
    if (!extra) continue;
    push(`EXTRA_${extra.code}`, fr ? extra.labelFr : extra.labelEn, 1, extra.priceCents);
    extraMinutes += extra.minutes;
  }

  const subtotalCents = lines.reduce((s, l) => s + l.totalCents, 0);
  const discountRatio = config.frequencyDiscounts[input.frequency] ?? 0;
  const discountCents = Math.round(subtotalCents * discountRatio);
  if (discountCents > 0) {
    lines.push({
      code: 'FREQUENCY_DISCOUNT',
      label: fr ? 'Rabais de fréquence' : 'Frequency discount',
      quantity: 1,
      unitCents: -discountCents,
      totalCents: -discountCents,
    });
  }
  const totalCents = Math.max(config.minimumPriceCents, roundCents(subtotalCents - discountCents, config.roundToCents));

  const m = config.minutes;
  const baseMinutes =
    m.base +
    input.bedrooms * m.perBedroom +
    input.bathrooms * m.perBathroom +
    input.kitchens * m.perKitchen +
    input.familyRooms * m.perFamilyRoom +
    Math.max(0, input.floors - 1) * m.perExtraFloor +
    (input.hasPets ? m.pets : 0);
  const estimatedMinutes = Math.ceil((baseMinutes * input.serviceDurationMultiplier + extraMinutes) / 15) * 15;
  const recommendedWorkers = estimatedMinutes > config.secondWorkerAboveMinutes ? 2 : 1;

  return { lines, subtotalCents, discountCents, totalCents, estimatedMinutes, recommendedWorkers };
}
